import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Pressable, ActivityIndicator, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { warmupScreenStyles as styles } from './WarmupScreen.styles';
import { getLayoutStyles } from '../../../appStyles/_layout.styles';
import { useTheme } from '../../../src/theme/useTheme';
import { useUserProfileOptional } from '../../../src/shared/contexts/UserProfileContext';
import { WarmupHeader } from '../WarmupHeader/WarmupHeader';
import { WarmupHub } from '../WarmupHub/WarmupHub';
import { WordleView } from '../WordleView/WordleView';
import { HigherLowerView } from '../HigherLowerView/HigherLowerView';
import { SlingshotView } from '../SlingshotView/SlingshotView';
import { KeeperDuelView } from '../KeeperDuelView/KeeperDuelView';
import { FootballerQuizView } from '../FootballerQuizView/FootballerQuizView';
import { WarmupIntroSheet } from '../WarmupIntroSheet/WarmupIntroSheet';
import { WordleResultOverlay } from '../WordleResultOverlay/WordleResultOverlay';
import { GameLeaderboardSheet } from '../GameLeaderboardSheet/GameLeaderboardSheet';
import { WarmupToast } from '../WarmupToast/WarmupToast';
import { useWordleGame } from '../../../src/quiz/hooks/useWordleGame';
import { shareResult } from '../../../src/quiz/helpers/shareResult.helper';
import { screenInsetStyle } from '../../../src/quiz/helpers/gameDynamicStyles.helper';
import { WARMUP_COLORS, WORDLE_GAME_KEY } from '../../../src/quiz/constants/warmup.constants';
import { HILO_GAME_KEY } from '../../../src/quiz/constants/higherLower.constants';
import { FOOTBALLER_GAME_KEY } from '../../../src/quiz/constants/footballerQuiz.constants';
import {
  WARMUP_INTRO_MODES,
  WARMUP_INTRO_GAME_KEY,
  type WarmupIntroMode,
} from '../../../src/quiz/constants/warmupIntros.constants';
import { LEADERBOARD_GAMES } from '../../../src/quiz/constants/gameLeaderboards.constants';
import { SLINGSHOT_GAME_KEY } from '../../../src/quiz/constants/slingshot.constants';
import { STRIKER_RUN_GAME_KEY } from '../../../src/quiz/constants/strikerRun.constants';
import { StrikerRunView } from '../StrikerRunView/StrikerRunView';
import { MAZE_STRIKER_GAME_KEY } from '../../../src/quiz/constants/mazeStriker.constants';
import { MazeStrikerView } from '../MazeStrikerView/MazeStrikerView';
import { MONKEY_WORLDS_GAME_KEY } from '../../../src/quiz/constants/monkeyWorlds.constants';
import { MonkeyWorldsView } from '../MonkeyWorldsView/MonkeyWorldsView';
import { MATCH_SPORT_GAME_KEY } from '../../../src/quiz/constants/matchSport.constants';
import { MatchSportView } from '../MatchSportView/MatchSportView';
import { BLOCKY_STRIKER_GAME_KEY } from '../../../src/quiz/constants/blockyStriker.constants';
import { BlockyStrikerView } from '../BlockyStrikerView/BlockyStrikerView';
import { KEEPER_DUEL_GAME_KEY } from '../../../src/quiz/constants/keeperDuel.constants';
import { useWarmupIntro } from '../../../src/quiz/hooks/useWarmupIntro';
import { useAnalytics } from '../../../src/analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../../src/analytics/events';

type WarmupView = 'hub' | 'wordle' | 'hilo' | 'slingshot' | 'footballer' | 'strikerRun' | 'blockyStriker' | 'keeperduel' | 'mazeStriker' | 'monkeyWorlds' | 'matchSport';

/** Views that have a first-run how-to-play intro (a subset of WarmupView). */
const INTRO_VIEWS = new Set<string>(WARMUP_INTRO_MODES);

/** Views that take over the whole screen — the app's bottom tab bar is hidden while they're open. */
const FULLSCREEN_VIEWS = new Set<WarmupView>(['slingshot', 'strikerRun', 'blockyStriker', 'keeperduel', 'mazeStriker', 'monkeyWorlds', 'matchSport']);

export function WarmupScreen() {
  const insets = useSafeAreaInsets();
  const isFocused = useIsFocused();
  const navigation = useNavigation();
  const theme = useTheme();
  const refetchProfile = useUserProfileOptional()?.refetchProfile;
  const { track } = useAnalytics();
  const game = useWordleGame();
  const [view, setView] = useState<WarmupView>('hub');
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const { introFor, maybeShowIntro, dismissIntro } = useWarmupIntro();

  // The app's real dark tab bar style — used to *restore* the bar after a fullscreen game. Passing
  // `undefined` would drop the navigator's styling and fall back to react-navigation's default
  // (white) bar, so we set the themed style back explicitly.
  const appTabBarStyle = useMemo(() => getLayoutStyles(theme, insets.bottom).tabBar, [theme, insets.bottom]);

  // The kicker (Wall Breaker) game is a fullscreen takeover: hide the app's bottom tab bar while
  // it's the active view, and restore the dark bar on the hub / other games. Done imperatively
  // per-screen (rather than via a global reactive style) because that reliably re-applies on web.
  useEffect(() => {
    navigation.setOptions({ tabBarStyle: FULLSCREEN_VIEWS.has(view) ? { display: 'none' } : appTabBarStyle });
  }, [navigation, view, appTabBarStyle]);

  // Coins bank server-side as you clear paying levels, and the header chip reads the shared economy
  // store — but the live in-game sync can be missed (offline blip, a display bound to the profile
  // object rather than the store). On returning to the hub from a game, pull the authoritative
  // profile so the balance always reflects what was actually banked this session.
  const prevViewRef = useRef(view);
  useEffect(() => {
    const prev = prevViewRef.current;
    prevViewRef.current = view;
    if (view === 'hub' && prev !== 'hub') void refetchProfile?.();
  }, [view, refetchProfile]);

  // Opening a specific game from the hub — the "how many actually play X" signal. First-timers also get
  // that mode's how-to-play intro (the hook no-ops for modes already seen, see useWarmupIntro).
  const openGame = useCallback((gameKey: string, target: WarmupView) => {
    track(ANALYTICS_EVENTS.GAME_STARTED, { game_key: gameKey });
    setView(target);
    if (INTRO_VIEWS.has(target)) maybeShowIntro(target as WarmupIntroMode);
  }, [track, maybeShowIntro]);

  // Fire the intro-shown signal exactly when an intro opens.
  useEffect(() => {
    if (introFor) track(ANALYTICS_EVENTS.GAME_INTRO_SHOWN, { game_key: WARMUP_INTRO_GAME_KEY[introFor] });
  }, [introFor, track]);

  const openLeaderboard = useCallback(() => {
    track(ANALYTICS_EVENTS.LEADERBOARD_OPENED, { game_key: LEADERBOARD_GAMES[0]?.gameKey });
    setLeaderboardOpen(true);
  }, [track]);

  const solved = game.status === 'WON' || game.status === 'LOST';

  // Let web users play with their physical keyboard while the Wordle is on screen — but only while the
  // games screen is actually focused, so typing on another tab never leaks into the (still-mounted) board.
  const { overlayOpen, onEnter, onDelete, onKeyPress } = game;
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!isFocused || view !== 'wordle' || overlayOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        onEnter();
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        onDelete();
      } else if (/^[a-zA-Z]$/.test(e.key)) {
        onKeyPress(e.key);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isFocused, view, overlayOpen, onEnter, onDelete, onKeyPress]);

  const handleShare = useCallback(async () => {
    if (!game.share) return;
    const result = await shareResult(game.share);
    if (result === 'copied') game.flashToast('Copied to clipboard 🙌');
    else if (result === 'failed') game.flashToast('Could not share');
    if (result !== 'failed') track(ANALYTICS_EVENTS.GAME_SHARED, { game_key: WORDLE_GAME_KEY });
  }, [game, track]);

  const renderContent = () => {
    if (game.isLoading) {
      return (
        <View style={styles.centered}>
          <ActivityIndicator color={WARMUP_COLORS.purple} />
        </View>
      );
    }
    if (game.isError) {
      return (
        <View style={styles.centered}>
          <Text style={styles.message}>Couldn&apos;t load today&apos;s game.</Text>
          <Pressable onPress={() => game.refetch()} style={({ pressed }) => [styles.retryButton, pressed && styles.pressed]}>
            <Text style={styles.retryText}>Retry</Text>
          </Pressable>
        </View>
      );
    }
    if (view === 'hub') {
      return (
        <WarmupHub
          wordleStreak={game.streak.current}
          wordleSolved={solved}
          wordleWon={game.status === 'WON'}
          wordleGuessesUsed={game.guessesUsed}
          wordleMaxGuesses={game.maxGuesses}
          onPlayWordle={() => openGame(WORDLE_GAME_KEY, 'wordle')}
          onPlayHilo={() => openGame(HILO_GAME_KEY, 'hilo')}
          onPlaySlingshot={() => openGame(SLINGSHOT_GAME_KEY, 'slingshot')}
          onPlayFootballer={() => openGame(FOOTBALLER_GAME_KEY, 'footballer')}
          onPlayStrikerRun={() => openGame(STRIKER_RUN_GAME_KEY, 'strikerRun')}
          onPlayBlockyStriker={() => openGame(BLOCKY_STRIKER_GAME_KEY, 'blockyStriker')}
          onPlayKeeperDuel={() => openGame(KEEPER_DUEL_GAME_KEY, 'keeperduel')}
          onPlayMazeStriker={() => openGame(MAZE_STRIKER_GAME_KEY, 'mazeStriker')}
          onPlayMonkeyWorlds={() => openGame(MONKEY_WORLDS_GAME_KEY, 'monkeyWorlds')}
          onPlayMatchSport={() => openGame(MATCH_SPORT_GAME_KEY, 'matchSport')}
        />
      );
    }
    if (view === 'hilo') {
      return <HigherLowerView onMoreGames={() => setView('hub')} />;
    }
    if (view === 'slingshot') {
      return <SlingshotView onMoreGames={() => setView('hub')} />;
    }
    if (view === 'footballer') {
      return <FootballerQuizView onMoreGames={() => setView('hub')} onLeaderboard={openLeaderboard} />;
    }
    return (
      <WordleView
        length={game.length}
        maxGuesses={game.maxGuesses}
        guesses={game.guesses}
        currentInput={game.currentInput}
        status={game.status}
        shakeNonce={game.shakeNonce}
        keyStates={game.keyStates}
        isSubmitting={game.isSubmitting}
        onKeyPress={game.onKeyPress}
        onEnter={game.onEnter}
        onDelete={game.onDelete}
      />
    );
  };

  // Fullscreen games (Wall Breaker, Striker Run) take over the whole screen — render them outside
  // the hub's header, top inset, and centered max-width column so nothing is clipped by chrome
  // (Wall Breaker also rotates to landscape). Each brings its own back button.
  if (FULLSCREEN_VIEWS.has(view)) {
    return (
      <View style={styles.fullscreenRoot}>
        {view === 'strikerRun' && <StrikerRunView onMoreGames={() => setView('hub')} />}
        {view === 'blockyStriker' && <BlockyStrikerView onMoreGames={() => setView('hub')} />}
        {view === 'keeperduel' && <KeeperDuelView onMoreGames={() => setView('hub')} />}
        {view === 'mazeStriker' && <MazeStrikerView onMoreGames={() => setView('hub')} />}
        {view === 'monkeyWorlds' && <MonkeyWorldsView onMoreGames={() => setView('hub')} />}
        {view === 'matchSport' && <MatchSportView onMoreGames={() => setView('hub')} />}
        {view === 'slingshot' && <SlingshotView onMoreGames={() => setView('hub')} />}
        {/* The how-to-play sheet must render here too — the early return above the hub layout
            otherwise defers a fullscreen game's intro until the player is back at the hub. */}
        <WarmupIntroSheet
          visible={!!introFor}
          mode={introFor}
          onPlay={dismissIntro}
          onSkip={dismissIntro}
        />
      </View>
    );
  }

  return (
    <View style={[styles.root, screenInsetStyle(insets.top)]}>
      <View style={styles.column}>
        <WarmupHeader
          showBack={view !== 'hub'}
          onBack={() => setView('hub')}
          showLeaderboard={view === 'hub'}
          onOpenLeaderboard={openLeaderboard}
        />

        <View style={styles.body}>{renderContent()}</View>

        <WordleResultOverlay
          visible={game.overlayOpen}
          won={game.status === 'WON'}
          reveal={game.reveal}
          shareGrid={game.share ?? ''}
          guessesUsed={game.guessesUsed}
          maxGuesses={game.maxGuesses}
          streak={game.streak}
          coinsAwarded={game.coinsAwarded}
          onShare={handleShare}
          onClose={() => game.setOverlayOpen(false)}
        />

        <WarmupToast message={game.toast} />

        <WarmupIntroSheet
          visible={!!introFor}
          mode={introFor}
          onPlay={dismissIntro}
          onSkip={dismissIntro}
        />

        <GameLeaderboardSheet visible={leaderboardOpen} onClose={() => setLeaderboardOpen(false)} />
      </View>
    </View>
  );
}
